import { useCallback, useMemo, useState } from 'react';

import { useProducts } from '../../../providers/ProductProvider';
import {
  resolveShopifyAdminProductId,
  shopifyGraphqlIdToAdminId,
} from '../../../utils/shopifyIds';
import { MAX_QUANTITY, MIN_QUANTITY } from '../constants';
import { CheckoutWorksController } from '../types';
import { buildCheckoutWork } from '../utils/workView';

const NO_ENTERED_WORKS: string[] = [];

const workQuantityLimit = (capacity: number | null | undefined) => {
  return capacity && capacity > 0 ? capacity : MAX_QUANTITY;
};

type UseBuyWorksArgs = {
  initialWorkIds: string[];
  productShopifyId: string;
  roomVariantIds: string[] | null;
};

export const useBuyWorks = ({
  initialWorkIds,
  productShopifyId,
  roomVariantIds,
}: UseBuyWorksArgs): CheckoutWorksController => {
  const { getProduct } = useProducts();
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [selectedIds, setSelectedIds] = useState<string[]>(
    initialWorkIds.length ? initialWorkIds : [productShopifyId],
  );

  const setMembers = useMemo(() => {
    const collectionSet =
      getProduct(productShopifyId)?.shopifyPurchaseDetails?.collectionSet ?? [];

    return collectionSet.flatMap((member) => {
      const id = resolveShopifyAdminProductId(member.shopifyId);
      if (!id || id === productShopifyId) return [];

      if (roomVariantIds) {
        const variantId = shopifyGraphqlIdToAdminId(member.shopifyVariantId);
        return roomVariantIds.includes(variantId)
          ? [{ id, soldOut: false }]
          : [];
      }

      return [{ id, soldOut: member.isSoldOut }];
    });
  }, [getProduct, productShopifyId, roomVariantIds]);

  const works = useMemo(() => {
    const productIds = [
      productShopifyId,
      ...setMembers.map((member) => member.id),
    ];
    const soldOutIds = new Set(
      setMembers.filter((member) => member.soldOut).map((member) => member.id),
    );

    return productIds.flatMap((productId) => {
      const item = getProduct(productId);
      if (!item) return [];

      const details = item.shopifyPurchaseDetails;

      return [
        {
          ...buildCheckoutWork(item),
          allowMultiple: Boolean(details?.allowMultipleProducts),
          quantity: quantities[productId] ?? MIN_QUANTITY,
          quantityLimit: workQuantityLimit(details?.maximumSalesCapacity),
          soldOut:
            soldOutIds.has(productId) ||
            !details?.shopifyVariantId ||
            details.available === false,
        },
      ];
    });
  }, [getProduct, productShopifyId, quantities, setMembers]);

  const worksById = useMemo(
    () => new Map(works.map((work) => [work.id, work])),
    [works],
  );

  const selectedWorkIds = useMemo(
    () => selectedIds.filter((id) => worksById.get(id)?.soldOut === false),
    [selectedIds, worksById],
  );

  const toggleWork = useCallback(
    (productId: string, quantity?: number) => {
      if (quantity !== undefined) {
        const clamped = Math.min(
          Math.max(Math.round(quantity), MIN_QUANTITY),
          worksById.get(productId)?.quantityLimit ?? MAX_QUANTITY,
        );
        setQuantities((previous) => ({ ...previous, [productId]: clamped }));
        return null;
      }

      if (works.length < 2 || worksById.get(productId)?.soldOut) return null;

      setSelectedIds((previous) =>
        previous.includes(productId)
          ? previous.filter((id) => id !== productId)
          : [...previous, productId],
      );

      return null;
    },
    [works.length, worksById],
  );

  return useMemo(
    () => ({
      enteredWorkIds: NO_ENTERED_WORKS,
      selectedWorkIds,
      toggleWork,
      works,
    }),
    [selectedWorkIds, toggleWork, works],
  );
};
